import { useEffect, useMemo, useRef, useState } from 'react';
import { useOnRobbieApplied } from '../../../contexts/RobbieUiContext';

/**
 * One context question the analysis engine still needs answered, exactly as
 * the analysis endpoint reports it. Questions with a detected value are
 * confirm-or-correct: Robbie's detection arrives pre-selected.
 */
export interface ContextQuestion {
  field: string;
  question: string;
  componentTypes?: string[];
  inputType?: string;
  /**
   * How `options` may be answered. 'multi_select' is a capability list (a servo
   * drive really does meet three standards at once), 'single_select' is one-of,
   * 'text' means there was nothing worth pre-listing. Older analysis runs have
   * no answerType, so the card falls back to single-select whenever options
   * exist — which is what it did before this field existed.
   */
  answerType?: 'single_select' | 'multi_select' | 'text';
  unit?: string;
  options?: string[];
  placeholder?: string;
  helpText?: string;
  isRobotCategoryConfirmation?: boolean;
  detectedValue?: string;
  /**
   * The component a question is about. Questions are built per BOM row, so
   * `field` is unique to its component (`<componentKey>_<specId>`) and is the
   * answer's key. BOM-wide questions (the robot category) and runs analysed
   * before per-row questions carry none of these.
   */
  componentIndex?: number;
  componentKey?: string;
  componentName?: string;
  specId?: string;
}

/**
 * A value the BOM already gave, so Robbie did not ask for it: the row's own
 * words (`bom_text`, with the words quoted in `evidence`) or our catalogue's
 * datasheet for an exact part (`catalogue`). Shown pre-filled and editable;
 * `field` is the answer's key, like a question's.
 */
export interface BomAnswer {
  field: string;
  componentIndex: number;
  componentKey: string;
  componentName: string;
  label: string;
  value: string;
  evidence?: string;
  source: 'bom_text' | 'catalogue';
}

/** A BOM row that names one exact orderable part: Robbie sources it as named and asks nothing. */
export interface ExactPart {
  componentIndex: number;
  componentKey: string;
  componentName: string;
  partNumber: string;
  source: 'bom_text' | 'catalogue';
}

/**
 * Owns the context-step answers: seeds detected values and the BOM's own
 * answers, tracks edits, and runs the save. The panel stays presentational.
 */
export const useContextStep = (
  questions: ContextQuestion[],
  onSubmit: (answers: Record<string, any>) => Promise<void>,
  bomAnswers: BomAnswer[] = [],
) => {
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Fields the buyer has touched — their answer always outranks a detection.
  const editedFields = useRef<Set<string>>(new Set());

  // The step mounts while the analysis is still running, so the questions (and
  // their detected values) arrive as a prop update, not on the first render.
  // Seeding therefore happens here rather than in the initial state, and it
  // only fills fields the buyer has not answered.
  useEffect(() => {
    const seeds: Array<[string, string]> = [
      ...questions.filter((q) => q.detectedValue).map((q): [string, string] => [q.field, q.detectedValue as string]),
      ...bomAnswers.map((a): [string, string] => [a.field, a.value]),
    ];
    setAnswers((prev) => {
      let next = prev;
      seeds.forEach(([field, value]) => {
        if (editedFields.current.has(field) || prev[field] !== undefined) return;
        if (next === prev) next = { ...prev };
        next[field] = value;
      });
      return next;
    });
  }, [questions, bomAnswers]);

  // A BOM answer the buyer corrected must be saved even when no question is left open.
  const [editedBomAnswer, setEditedBomAnswer] = useState(false);
  const bomAnswerFields = useMemo(() => new Set(bomAnswers.map((a) => a.field)), [bomAnswers]);

  const setAnswer = (field: string, value: any) => {
    editedFields.current.add(field);
    if (bomAnswerFields.has(field)) setEditedBomAnswer(true);
    setAnswers((prev) => ({ ...prev, [field]: value }));
  };

  // A Robbie card the buyer applied is already saved; its answers go into the
  // questions as the buyer's own, so a later Continue keeps them.
  useOnRobbieApplied('context_answers', (event) => {
    Object.entries(event.answers).forEach(([field, value]) => setAnswer(field, value));
  });

  const submit = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await onSubmit(answers);
    } catch (err: any) {
      setError(err?.message || 'I could not save your answers — try again in a moment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const answeredCount = questions.filter((q) => {
    const v = answers[q.field];
    return v !== undefined && v !== null && String(v).trim() !== '';
  }).length;

  return { answers, setAnswer, submit, isSubmitting, error, answeredCount, editedBomAnswer };
};

export default useContextStep;
